import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar, MapPin, Share2, Navigation } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeLinkUrl } from '@/src/modules/renderer/url-safety';
import { ScratchReveal } from '../_shared/ScratchReveal';
import { GoldLeafBurst, SandDrift } from '../_shared/WowEffects';
import { EnvelopeUnfold } from '../_shared/SignatureEffects';
import { coupleMonogram } from '../_shared/SacredArtifacts';
import { pickWeddingStr, resolveCouplePhotos, resolveExpMeta } from '../_shared/octoberWeddingFields';
import '../_shared/wow-effects.css';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-scratch-std-goldensand',
  name: 'Golden Sand — Scratch Save the Date',
  occasionType: 'SAVE_THE_DATE',
  description:
    'Interactive Save-the-Date: scratch gold-foil sand to reveal couple photo, names, wedding date, and venue.',
  isPremium: true,
  tags: ['save-the-date', 'scratch', 'interactive', 'canvas', 'teaser', 'premium'],
  colorScheme: { primary: '#C9A227', accent: '#5C3A1E', bg: '#FFF8E7' },
};

const FOIL = '/assets/october-templates/goldensand-foil.jpg';
const STATIONERY = '/assets/october-templates/goldensand-stationery.jpg';

function useCountdown(targetMs: number | null) {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (targetMs == null || Number.isNaN(targetMs)) return;
    const tick = () => {
      const diff = targetMs - Date.now();
      const next =
        diff <= 0
          ? { d: 0, h: 0, m: 0, s: 0 }
          : {
              d: Math.floor(diff / 86400000),
              h: Math.floor((diff / 3600000) % 24),
              m: Math.floor((diff / 60000) % 60),
              s: Math.floor((diff / 1000) % 60),
            };
      setLeft((prev) =>
        prev.d === next.d && prev.h === next.h && prev.m === next.m && prev.s === next.s
          ? prev
          : next,
      );
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [targetMs]);
  return left;
}

export function ScratchStdGoldensand({ data }: { data: InvitationData }) {
  const [revealed, setRevealed] = useState(false);
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);
  const expMeta = resolveExpMeta(data);
  const { couplePhoto } = resolveCouplePhotos(data);

  const groomName = pickWeddingStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    extracted?.groom,
  );
  const brideName = pickWeddingStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || '';

  const weddingEventName = pickWeddingStr(data.eventName);

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickWeddingStr(data.eventDate);

  const venue = pickWeddingStr(data.eventVenue, wedding?.weddingEvents?.[0]?.venue);
  const countdown = useCountdown(parsedDate ? parsedDate.getTime() : null);
  const mono = coupleMonogram(coupleTitle || weddingEventName || 'STD');
  const mapsUrl = venue
    ? safeLinkUrl(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`)
    : '';

  const share = async () => {
    const text = `Save the Date — ${coupleTitle || weddingEventName}${eventDateLabel ? ` · ${eventDateLabel}` : ''}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Save the Date', text, url: window.location.href });
      } catch {
        /* cancelled */
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${text} ${window.location.href}`);
    }
  };

  return (
    <div className="ssd-root" data-template="exp-scratch-std-goldensand">
      <GoldLeafBurst active={revealed} />

      <div className="ssd-stage">
        <div className="ssd-hero-art" style={{ backgroundImage: `url(${STATIONERY})` }} aria-hidden />
        <div className="ssd-hero-veil" aria-hidden />
        <SandDrift count={revealed ? 28 : 18} />

        <EnvelopeUnfold active={revealed}>
          <div className="ssd-paper">
            <header className="ssd-header">
              <p className="ssd-eyebrow">Save the Date</p>
              <p className="ssd-hint-line">Scratch the foil to uncover the muhurat</p>
            </header>

            <div className="ssd-card">
              <ScratchReveal
                className="ssd-scratch"
                height={revealed ? 420 : 360}
                coverImageUrl={FOIL}
                hint="Scratch the gold foil"
                onReveal={() => setRevealed(true)}
              >
                <div className="ssd-reveal">
                  {couplePhoto ? (
                    <div className="ssd-couple-photo">
                      <img src={couplePhoto} alt={coupleTitle || 'The couple'} />
                    </div>
                  ) : (
                    <div className="ssd-couple-photo ssd-couple-photo--empty" aria-label="Couple photo">
                      <span>Couple photo</span>
                      <small>Upload in Details</small>
                    </div>
                  )}

                  <p className="ssd-mono">{coupleTitle || weddingEventName ? mono : 'A&B'}</p>
                  <p className={`ssd-couple${coupleTitle ? '' : ' ssd-placeholder'}`}>
                    {coupleTitle || 'Couple names — edit in Details'}
                  </p>
                  <p className={`ssd-event-name${weddingEventName ? '' : ' ssd-placeholder'}`}>
                    {weddingEventName || 'Wedding event name — edit in Details'}
                  </p>

                  <p className={`ssd-date${eventDateLabel ? '' : ' ssd-placeholder'}`}>
                    <Calendar size={15} /> {eventDateLabel || 'Date — set in Details'}
                  </p>
                  <p className={`ssd-venue${venue ? '' : ' ssd-placeholder'}`}>
                    <MapPin size={15} /> {venue || 'Venue / pin — set in Details'}
                  </p>
                  {mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" className="ssd-pin-link">
                      <Navigation size={13} /> Open map pin
                    </a>
                  ) : null}
                </div>
              </ScratchReveal>
            </div>

            <AnimatePresence>
              {revealed ? (
                <motion.div
                  className="ssd-after ssd-after--compact"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.1 }}
                >
                  {parsedDate ? (
                    <div className="ssd-countdown" aria-label="Countdown">
                      {[
                        ['Days', countdown.d],
                        ['Hrs', countdown.h],
                        ['Min', countdown.m],
                        ['Sec', countdown.s],
                      ].map(([label, value]) => (
                        <div key={String(label)} className="ssd-tick">
                          <strong>{value}</strong>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <button type="button" className="ssd-share" onClick={() => void share()}>
                    <Share2 size={16} /> Share Save the Date
                  </button>
                </motion.div>
              ) : (
                <p className="ssd-tease">Date · event · pin hide under the gold…</p>
              )}
            </AnimatePresence>
          </div>
        </EnvelopeUnfold>
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: ScratchStdGoldensand });
